// Fiche du personnage : portrait, caractéristiques, combat et ressources, compétences, sorts à lancer hors combat,
// conditions, posture d'absence, histoire repliable et règles du serment (flamme).
import { useState } from "preact/hooks";
import type {
  ActiveCondition, AttributeId, CharacterView, ClientView, ConditionId, Id, LifeState, Posture, SkillId, SpellDef, SpellKind,
} from "../../shared/types";
import { ATTRIBUTES, MINUTE } from "../../shared/types";
import { send } from "../store";
import { Portrait } from "./Portrait";
import "./Sheet.css";

export interface SheetProps { view: ClientView }

type OtherHero = ClientView["othersHere"][number];
type ResourceKind = "hp" | "mana" | "energy" | "hunger";

const MAX_LEVEL = 20;
const FLAME_MAX = 3;
const TOP_SKILLS = 4;

const ATTR_LABELS: Record<AttributeId, string> = {
  FOR: "Force",
  DEX: "Dextérité",
  CON: "Constitution",
  INT: "Intelligence",
  SAG: "Sagesse",
  CHA: "Charisme",
};

const SKILL_LABELS: Record<SkillId, string> = {
  athletisme: "Athlétisme",
  acrobaties: "Acrobaties",
  discretion: "Discrétion",
  escamotage: "Escamotage",
  arcanes: "Arcanes",
  histoire: "Histoire",
  investigation: "Investigation",
  nature: "Nature",
  perception: "Perception",
  survie: "Survie",
  medecine: "Médecine",
  intuition: "Intuition",
  persuasion: "Persuasion",
  tromperie: "Tromperie",
  intimidation: "Intimidation",
  representation: "Représentation",
};
const SKILL_ORDER = Object.keys(SKILL_LABELS) as SkillId[];

const CONDITION_INFO: Record<ConditionId, { label: string; text: string }> = {
  empoisonne: { label: "Empoisonné", text: "Le poison ronge tes forces : désavantage aux attaques, et 1d4 de dégâts à chaque round de combat." },
  saignement: { label: "Saignement", text: "Une plaie ouverte te coûte 1d4 PV à chaque round." },
  etourdi: { label: "Étourdi", text: "La tête tourne : désavantage à tous tes tests." },
  beni: { label: "Béni", text: "Une faveur divine te porte : avantage à tes tests." },
  affame: { label: "Affamé", text: "La faim t’affaiblit : désavantage aux tests de Force et de Constitution." },
  famine: { label: "Famine", text: "Rien à manger : désavantage à tout, et 1 PV perdu par heure." },
  epuise: { label: "Épuisé", text: "Plus d’énergie : désavantage à tout, et seules les actions sans coût restent possibles." },
  invisible: { label: "Invisible", text: "Nul ne te remarque vraiment : avantage à tes attaques." },
  protege: { label: "Protégé", text: "Une protection t’entoure : +2 à ta défense (CA)." },
  enflamme: { label: "En flammes", text: "Tu brûles : 1d6 de dégâts à chaque round." },
  gele: { label: "Gelé", text: "Le froid engourdit tes gestes : désavantage aux tests de Dextérité." },
  maudit: { label: "Maudit", text: "Une malédiction pèse sur toi : désavantage à tous tes tests." },
  inspire: { label: "Inspiré", text: "Une inspiration t’anime : avantage à tes tests." },
  cicatrice_oubli: { label: "Cicatrice de l’oubli", text: "Ton retour a laissé une trace : −2 à tous tes tests." },
  repose: { label: "Reposé", text: "Un bon sommeil t’a remis d’aplomb." },
};

const LIFE_LABELS: Partial<Record<LifeState, string>> = {
  endormi: "Endormi",
  en_route: "En route",
  agonie: "Agonie",
  egare: "Égaré",
  mort: "Mort",
};

const SPELL_KIND_LABELS: Record<SpellKind, string> = {
  degats: "Dégâts",
  soin: "Soin",
  buff: "Bienfait",
  debuff: "Entrave",
  utilitaire: "Utilitaire",
};

const POSTURES: { id: Posture; label: string; icon: string; text: string }[] = [
  {
    id: "prudent",
    label: "Prudent",
    icon: "🛡️",
    text: "Ton personnage se repose et regagne des PV. Si le danger du lieu atteint 3 ou plus, il rentre vers un lieu sûr.",
  },
  {
    id: "travailleur",
    label: "Travailleur",
    icon: "🔨",
    text: "Dans un village, il gagne 2d6 + son niveau en pièces d’or, et parfois du pain. Ailleurs, il se comporte comme un prudent.",
  },
  {
    id: "explorateur",
    label: "Explorateur",
    icon: "🧭",
    text: "Il gagne un peu d’expérience, fait des trouvailles et découvre parfois un lieu voisin. Il fait face à un risque de rencontre de 10 % + 8 % par point de danger du lieu.",
  },
];

const clockFmt = new Intl.DateTimeFormat("fr-FR", {
  timeZone: "Europe/Paris",
  weekday: "long",
  day: "numeric",
  month: "long",
  hour: "2-digit",
  minute: "2-digit",
});

/** « +2 » ou « −1 » (signe typographique). */
const signed = (n: number) => (n < 0 ? `−${Math.abs(n)}` : `+${n}`);

/** Pourcentage borné entre 0 et 100. */
function ratio(value: number, max: number): number {
  if (max <= 0) return 0;
  return Math.min(100, Math.max(0, (value / max) * 100));
}

/** Durée lisible : « 45 min », « 3 h 20 », « 2 j 4 h ». */
function formatDuration(ms: number): string {
  const minutes = Math.max(1, Math.ceil(ms / MINUTE));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  if (hours < 24) return restMinutes ? `${hours} h ${restMinutes}` : `${hours} h`;
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours ? `${days} j ${restHours} h` : `${days} j`;
}

export function Sheet(props: SheetProps) {
  const c = props.view.character;
  if (!c) {
    return (
      <section class="sheet sheet-empty">
        <p class="sheet-empty-text">Ton personnage n’a pas encore pris forme. Crée-le pour découvrir sa fiche.</p>
      </section>
    );
  }
  const now = props.view.now;
  return (
    <article class="sheet" aria-label={`Fiche de ${c.name}`}>
      <SheetHeader c={c} />
      <AttributesBlock c={c} />
      <CombatBlock c={c} />
      <SkillsBlock c={c} />
      <SpellsBlock c={c} others={props.view.othersHere} />
      <ConditionsBlock c={c} now={now} />
      <PostureBlock posture={c.posture} />
      <StoryBlock backstory={c.backstory} />
      <OathBlock c={c} now={now} />
    </article>
  );
}

function SheetHeader({ c }: { c: CharacterView }) {
  const life = LIFE_LABELS[c.lifeState];
  const maxed = c.level >= MAX_LEVEL;
  const xpRatio = maxed ? 100 : ratio(c.xp - c.xpForCurrent, c.xpForNext - c.xpForCurrent);
  const xpLeft = Math.max(0, c.xpForNext - c.xp);
  return (
    <header class="sheet-hero">
      <div class="sheet-head">
        <Portrait classId={c.classId} color={c.appearance.color} sigil={c.appearance.sigil} size={96} dead={c.lifeState === "mort"} />
        <div class="sheet-head-text">
          <h2 class="sheet-name">{c.name}</h2>
          <p class="sheet-class">
            <span>{c.className} · niveau {c.level}</span>
            {life && <span class="badge sheet-life">{life}</span>}
          </p>
          {c.concept && <p class="sheet-concept">{c.concept}</p>}
        </div>
      </div>
      <div class="sheet-xp">
        <div
          class="bar sheet-xp-bar"
          role="meter"
          aria-label="Expérience"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(xpRatio)}
        >
          <div class="bar-fill" style={{ width: `${xpRatio}%` }} />
        </div>
        <p class="sheet-xp-text">
          {maxed ? `${c.xp} XP · niveau maximum` : `${c.xp} XP · encore ${xpLeft} pour le niveau ${c.level + 1}`}
        </p>
      </div>
    </header>
  );
}

function AttributesBlock({ c }: { c: CharacterView }) {
  return (
    <section class="sheet-block">
      <h3 class="sheet-title">Caractéristiques</h3>
      <ul class="sheet-attrs">
        {ATTRIBUTES.map((a) => (
          <li key={a} class="sheet-attr">
            <span class="sheet-attr-name">{ATTR_LABELS[a]}</span>
            <span class="sheet-attr-mod">{signed(c.modifiers[a])}</span>
            <span class="sheet-attr-score">Score {c.attributes[a]}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CombatBlock({ c }: { c: CharacterView }) {
  return (
    <section class="sheet-block">
      <h3 class="sheet-title">Combat et ressources</h3>
      <dl class="sheet-combat">
        <div class="sheet-stat">
          <dt>Défense (CA)</dt>
          <dd>{c.ca}</dd>
        </div>
        <div class="sheet-stat">
          <dt>Attaque</dt>
          <dd>{signed(c.attackBonus)}</dd>
        </div>
        <div class="sheet-stat">
          <dt>Maîtrise</dt>
          <dd>{signed(c.proficiency)}</dd>
        </div>
      </dl>
      <ul class="sheet-resources">
        <Resource kind="hp" label="PV" value={c.hp} max={c.hpMax} />
        <Resource kind="mana" label="Mana" value={c.mana} max={c.manaMax} />
        <Resource kind="energy" label="Énergie" value={c.energy} max={c.energyMax} />
        <Resource kind="hunger" label="Satiété" value={c.satiety} max={100} />
        <li class="sheet-res sheet-res-flame">
          <div class="sheet-res-head">
            <span>Flamme</span>
            <span class="sheet-res-value">{c.flame} / {FLAME_MAX}</span>
          </div>
          <div class="sheet-pips" aria-hidden="true">
            {Array.from({ length: FLAME_MAX }, (_, i) => (
              <span key={i} class={i < c.flame ? "sheet-pip is-lit" : "sheet-pip"}>🔥</span>
            ))}
          </div>
        </li>
      </ul>
    </section>
  );
}

function Resource(props: { kind: ResourceKind; label: string; value: number; max: number }) {
  const { kind, label, value, max } = props;
  return (
    <li class={`sheet-res sheet-res-${kind}`}>
      <div class="sheet-res-head">
        <span>{label}</span>
        <span class="sheet-res-value">{value} / {max}</span>
      </div>
      <div class="bar" role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={value}>
        <div class="bar-fill" style={{ width: `${ratio(value, max)}%` }} />
      </div>
    </li>
  );
}

function SkillsBlock({ c }: { c: CharacterView }) {
  // Tri stable : à égalité, l'ordre du catalogue est conservé.
  const sorted = [...SKILL_ORDER].sort((a, b) => c.skills[b] - c.skills[a]);
  const best = new Set<SkillId>(sorted.slice(0, TOP_SKILLS));
  return (
    <section class="sheet-block">
      <h3 class="sheet-title">Compétences</h3>
      <ul class="sheet-skills">
        {sorted.map((s) => (
          <li key={s} class={best.has(s) ? "sheet-skill sheet-skill-best" : "sheet-skill"}>
            <span class="sheet-skill-name">{SKILL_LABELS[s]}</span>
            <span class="sheet-skill-bonus">{signed(c.skills[s])}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SpellsBlock({ c, others }: { c: CharacterView; others: OtherHero[] }) {
  return (
    <section class="sheet-block">
      <h3 class="sheet-title">Sorts</h3>
      {c.spells.length === 0 ? (
        <p class="muted">Aucun sort pour l’instant.</p>
      ) : (
        <ul class="sheet-spells">
          {c.spells.map((spell) => (
            <SpellCard key={spell.key} spell={spell} c={c} others={others} />
          ))}
        </ul>
      )}
    </section>
  );
}

function SpellCard({ spell, c, others }: { spell: SpellDef; c: CharacterView; others: OtherHero[] }) {
  const [chosen, setChosen] = useState<Id>(c.id);
  // Si la cible choisie n'est plus présente, on retombe sur soi.
  const target = others.some((o) => o.id === chosen) ? chosen : c.id;
  const canAfford = c.mana >= spell.mana;
  const showTarget = spell.target !== "soi" && others.length > 0;

  return (
    <li class="sheet-spell">
      <div class="sheet-spell-head">
        <span class="sheet-spell-icon" aria-hidden="true">{spell.icon}</span>
        <div class="sheet-spell-title">
          <h4 class="sheet-spell-name">{spell.name}</h4>
          <p class="sheet-spell-meta">
            Dès le niveau {spell.level} · {spell.mana} mana · {SPELL_KIND_LABELS[spell.kind]}
          </p>
        </div>
      </div>
      <p class="sheet-spell-desc">{spell.description}</p>
      {spell.combatOnly ? (
        <p class="sheet-spell-note">Ce sort ne se lance qu’en combat.</p>
      ) : (
        <div class="sheet-spell-cast">
          {showTarget && (
            <label class="sheet-spell-target">
              <span>Cible</span>
              <select class="input" value={target} onChange={(e) => setChosen(e.currentTarget.value)}>
                <option value={c.id}>Moi</option>
                {others.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </label>
          )}
          <button
            type="button"
            class="btn btn-primary btn-small sheet-spell-go"
            disabled={!canAfford}
            onClick={() => send({ t: "cast", spellKey: spell.key, targetCharacterId: target })}
          >
            Lancer
          </button>
          {!canAfford && <p class="sheet-spell-note">Mana insuffisante (il t’en faut {spell.mana}).</p>}
        </div>
      )}
    </li>
  );
}

function ConditionsBlock({ c, now }: { c: CharacterView; now: number }) {
  return (
    <section class="sheet-block">
      <h3 class="sheet-title">Conditions</h3>
      {c.conditions.length === 0 ? (
        <p class="muted">Rien à signaler : ton personnage est en forme.</p>
      ) : (
        <ul class="sheet-conditions">
          {c.conditions.map((cond, i) => (
            <ConditionItem key={`${cond.id}-${i}`} cond={cond} now={now} />
          ))}
        </ul>
      )}
    </section>
  );
}

function ConditionItem({ cond, now }: { cond: ActiveCondition; now: number }) {
  const info = CONDITION_INFO[cond.id];
  const left =
    cond.until === null ? "jusqu’à retrait" : cond.until <= now ? "se dissipe" : `encore ${formatDuration(cond.until - now)}`;
  return (
    <li class="sheet-condition">
      <div class="sheet-condition-head">
        <span class="sheet-condition-name">{info.label}</span>
        <span class="sheet-condition-time">{left}</span>
      </div>
      <p class="sheet-condition-text">{info.text}</p>
    </li>
  );
}

function PostureBlock({ posture }: { posture: Posture }) {
  const current = POSTURES.find((p) => p.id === posture) ?? POSTURES[0];
  return (
    <section class="sheet-block">
      <h3 class="sheet-title">Posture d’absence</h3>
      <p class="sheet-hint">Si tu n’agis pas depuis 6 h, ton personnage suit cette routine, toutes les 6 h.</p>
      <div class="sheet-posture" role="group" aria-label="Posture d’absence">
        {POSTURES.map((p) => (
          <button
            key={p.id}
            type="button"
            class="sheet-posture-btn"
            aria-pressed={p.id === posture}
            onClick={() => {
              if (p.id !== posture) send({ t: "set_posture", posture: p.id });
            }}
          >
            <span class="sheet-posture-icon" aria-hidden="true">{p.icon}</span>
            <span>{p.label}</span>
          </button>
        ))}
      </div>
      <p class="sheet-posture-text">{current.text}</p>
    </section>
  );
}

function StoryBlock({ backstory }: { backstory: string }) {
  const paragraphs = backstory.split(/\n+/).map((p) => p.trim()).filter((p) => p.length > 0);
  return (
    <details class="sheet-block sheet-story">
      <summary class="sheet-story-summary">Histoire</summary>
      {paragraphs.length === 0 ? (
        <p class="sheet-story-empty">Son passé reste à écrire.</p>
      ) : (
        paragraphs.map((p, i) => (
          <p key={i} class="sheet-story-text">{p}</p>
        ))
      )}
    </details>
  );
}

function OathBlock({ c, now }: { c: CharacterView; now: number }) {
  const left = c.driftAt - now;
  return (
    <section class="sheet-block sheet-oath">
      <h3 class="sheet-title">Règles du serment</h3>
      <p class="sheet-oath-deadline">
        {left > 0
          ? `Pour ne pas dériver, agis avant ${clockFmt.format(new Date(c.driftAt))} (encore ${formatDuration(left)}).`
          : "Le délai est passé : agir rallume une flamme (trois au maximum)."}
      </p>
      <ol class="sheet-oath-rules">
        <li>Chaque tranche de 24 h sans action volontaire éteint une flamme.</li>
        <li>À 0 flamme, ton personnage s’égare : il erre comme un PNJ dans le récit des autres.</li>
        <li>
          À ta prochaine action, il revient (« rappel ») avec une flamme et une cicatrice de l’oubli (72 h, −2 à tous les tests).
        </li>
        <li>Agir après plus de 12 h d’inactivité rallume une flamme, jusqu’à 3.</li>
      </ol>
      {c.lifeState === "egare" && (
        <p class="sheet-oath-alert">Ton personnage est égaré. Ta prochaine action le ramènera.</p>
      )}
    </section>
  );
}
